// Efficiency Metrics Module - Pure statistical analysis functions
// No caching, no database queries - just calculations

import { ParameterInfo } from './telemetryService';

export interface ConsistencyMetrics {
  consistency: number;
  coefficientOfVariation: number;
}

export interface CrewBalanceMetrics {
  balance: number;
  powerRange: { min: number; max: number };
}

// Functions will be implemented in subsequent tasks
export function calculateEfficiency(telemetryData: any[], _parameters?: ParameterInfo): number {
  if (!telemetryData || telemetryData.length < 4) {
    return 0;
  }

  // POWER TRANSFER EFFICIENCY - FIXED VALUE
  // 35% represents typical elite rowing power transfer efficiency:
  // - Blade efficiency: ~85% (water interaction losses)
  // - Biomechanical efficiency: ~45% (technique and muscle efficiency)
  // - System efficiency: ~95% (oar flex and rigging losses)
  // - Overall: 85% × 45% × 95% ≈ 35%

  return 0.35; // 35% power transfer efficiency for elite rowing
}

export function calculateConsistencyMetrics(powerData: number[]): ConsistencyMetrics {
  if (powerData.length < 2) {
    return { consistency: 0, coefficientOfVariation: 1 };
  }

  const mean = powerData.reduce((sum, p) => sum + p, 0) / powerData.length;
  const variance = powerData.reduce((sum, p) => sum + Math.pow(p - mean, 2), 0) / powerData.length;
  const stdDev = Math.sqrt(variance);

  // Coefficient of variation (lower is more consistent)
  const coefficientOfVariation = mean > 0 ? stdDev / mean : 1;

  // Convert to consistency percentage (higher is more consistent)
  const consistency = Math.max(0, Math.min(100, (1 - coefficientOfVariation) * 100));

  return {
    consistency: Math.round(consistency),
    coefficientOfVariation: Math.round(coefficientOfVariation * 1000) / 1000, // Round to 3 decimal places
  };
}

export function calculateCrewBalance(rowerPowers: {
  [rower: number]: number[];
}): CrewBalanceMetrics {
  const rowerAverages = Object.values(rowerPowers)
    .map((powers) => {
      if (powers.length === 0) return 0;
      return powers.reduce((sum, p) => sum + p, 0) / powers.length;
    })
    .filter((avg) => avg > 0);

  if (rowerAverages.length === 0) {
    return { balance: 0, powerRange: { min: 0, max: 0 } };
  }

  const powerRange = {
    min: Math.min(...rowerAverages),
    max: Math.max(...rowerAverages),
  };

  // Calculate balance as inverse of power range relative to average
  const averagePower = rowerAverages.reduce((sum, avg) => sum + avg, 0) / rowerAverages.length;
  const powerSpread = powerRange.max - powerRange.min;
  const balance = averagePower > 0 ? Math.max(0, 100 - (powerSpread / averagePower) * 100) : 0;

  return {
    balance: Math.round(balance),
    powerRange: {
      min: Math.round(powerRange.min),
      max: Math.round(powerRange.max),
    },
  };
}
